/**
 * Pure helpers for the asset preview modal. The modal component renders
 * UI; these helpers shape the data — prev/next navigation, filename
 * derivation, formatted dates, MIME-from-format. Kept pure so they're
 * testable without rendering.
 */

import type { AssetSummary } from '../../domain';

/**
 * Find the previous or next asset in the list, relative to the asset
 * with `currentId`. Returns null when there is no adjacent asset
 * (at an edge, or the current asset is missing from the list).
 *
 * Does NOT wrap — reaching the start or end stops, which keeps the
 * arrow affordance honest (the disabled state matches the absence of
 * a target).
 */
export function findAdjacentAsset(
  assets: AssetSummary[],
  currentId: string,
  direction: 'prev' | 'next',
): AssetSummary | null {
  if (assets.length === 0) return null;
  const idx = assets.findIndex(a => a.assetId === currentId);
  if (idx === -1) return null;
  const target = direction === 'prev' ? idx - 1 : idx + 1;
  if (target < 0 || target >= assets.length) return null;
  return assets[target];
}

/**
 * Derive a download filename from an asset. Prefers a slugged prompt
 * (capped at 40 chars), then the subType, then a generic "asset"
 * fallback — always suffixed with the assetId's first 8 chars to
 * disambiguate, plus an extension from the asset's format.
 *
 * Examples:
 *   prompt "A red ferrari in the snow" -> "a-red-ferrari-in-the-snow-abcd1234.png"
 *   no prompt, subType "Generated"     -> "generated-abcd1234.png"
 *   nothing                             -> "asset-abcd1234.png"
 */
export function filenameForAsset(asset: AssetSummary): string {
  const stem = slugify(asset.promptUsed) || slugify(asset.subType) || 'asset';
  const idSuffix = (asset.assetId || '').replace(/-/g, '').slice(0, 8) || 'unknown';
  const ext = extensionForAsset(asset);
  return `${stem}-${idSuffix}.${ext}`;
}

/**
 * Slug a free-text string into a lowercase-hyphen-only token, capped
 * at 40 characters at a word boundary when possible. Returns empty
 * string for nullish / whitespace-only input.
 */
export function slugify(text: string | undefined | null, maxLength = 40): string {
  if (!text) return '';
  const cleaned = text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // strip punctuation
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  if (cleaned.length <= maxLength) return cleaned;
  // Cut at the last hyphen before maxLength so we don't end mid-word.
  const truncated = cleaned.slice(0, maxLength);
  const lastDash = truncated.lastIndexOf('-');
  return lastDash > maxLength / 2 ? truncated.slice(0, lastDash) : truncated;
}

/**
 * Pick a file extension for an asset based on its `metadata.format`,
 * `metadata.mimeType`, or URL — falling back to "png" when nothing
 * useful is available. Always lowercase, no leading dot.
 */
export function extensionForAsset(asset: AssetSummary): string {
  // metadata.format like "Png" / "Jpeg" / "Webp" / "Mp4". `metadata` was widened
  // to `Record<string, unknown>` on 2026-05-24 (BUG-002) so we narrow to string
  // here before using string-only methods.
  const fmtRaw = asset.metadata?.format;
  const fmt = typeof fmtRaw === 'string' ? fmtRaw.toLowerCase() : undefined;
  if (fmt) return normalizeExtension(fmt);
  // mimeType like "image/png" / "video/mp4"
  const mimeRaw = asset.metadata?.mimeType;
  const mime = typeof mimeRaw === 'string' ? mimeRaw.toLowerCase() : undefined;
  if (mime) {
    const slash = mime.indexOf('/');
    if (slash !== -1) return normalizeExtension(mime.slice(slash + 1));
  }
  // URL pathname extension as a last resort
  const urlExt = extensionFromUrl(asset.url);
  if (urlExt) return urlExt;
  if (asset.kind === 'video') return 'mp4';
  if (asset.kind === 'document') return 'md';
  return 'png';
}

/** Whether an asset is a Writer's Lab document (kind === 'document'). */
export function isDocumentAsset(asset: AssetSummary): boolean {
  return asset.kind === 'document';
}

// Candidate metadata fields that may carry a document's text/preview. The
// exact field is pending BE confirmation of document-content delivery (tracked
// in docs/architectureDesign.md); this reads the likely ones and falls back to
// the prompt, so the gallery degrades gracefully either way.
const DOC_TEXT_FIELDS = ['textContent', 'text', 'content', 'preview', 'snippet'] as const;

/** Best available text for a document asset (metadata text field → prompt → ''). */
export function documentText(asset: AssetSummary): string {
  const md = asset.metadata as Record<string, unknown> | undefined;
  if (md) {
    for (const field of DOC_TEXT_FIELDS) {
      const value = md[field];
      if (typeof value === 'string' && value.trim()) return value;
    }
  }
  return asset.promptUsed?.trim() ?? '';
}

/** A single-line snippet of a document's text, capped at `maxLen` chars. */
export function documentSnippet(asset: AssetSummary, maxLen = 120): string {
  const text = documentText(asset).replace(/\s+/g, ' ').trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen).trimEnd()}…`;
}

function normalizeExtension(raw: string): string {
  const cleaned = raw.replace(/^\.+/, '').toLowerCase();
  if (cleaned === 'jpeg') return 'jpg';
  return cleaned;
}

function extensionFromUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, 'http://anchor.invalid');
    const path = parsed.pathname;
    const dot = path.lastIndexOf('.');
    if (dot === -1 || dot === path.length - 1) return null;
    const ext = path.slice(dot + 1).toLowerCase();
    // Reject obvious garbage (paths with slashes after the dot)
    if (ext.includes('/') || ext.length > 8) return null;
    return normalizeExtension(ext);
  } catch {
    return null;
  }
}

/**
 * Format an ISO date string as a short, human-readable local date+time.
 * Returns "Unknown" for invalid / missing input — keeps the metadata
 * row populated rather than blank.
 */
export function formatTimestamp(iso: string | undefined | null): string {
  if (!iso) return 'Unknown';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return 'Unknown';
  const d = new Date(ms);
  // e.g. "May 15, 2026 · 5:23 PM"
  const datePart = d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const timePart = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${datePart} · ${timePart}`;
}
